import { useIntl } from 'react-intl';
import {
  CreateOidcIdentityProviderDto,
  CreateSamlIdentityProviderDto,
  IdentityProviderType,
  OidcAuthFlow,
} from '../../../../../api-client';
import { CreateIdentityProviderModalComponent } from './CreateIdentityProviderModal.types';
import { Box, Button, Flexbox, Icon, Link, Modal, Select, Typography } from '@hiiretail/synergy-ui';
import { ErrorBox } from '../../../../../common/components/ErrorBox/ErrorBox';
import { IdentityProvidersMessages } from '../../../IdentityProviders.messages';
import { CommonMessages } from '../../../../../common';
import { CreateSamlProviderForm } from './CreateSamlProviderForm/CreateSamlProviderForm';
import { useCreateSamlProviderForm } from '../../../hooks/Saml/useCreateSamlProviderForm';
import { useFieldArray } from 'react-hook-form';
import { useState, useMemo } from 'react';
import { Option } from '@hiiretail/synergy-ui/dist/types/components/inputs/Select/Select.types';
import { CreateOidcProviderForm } from './CreateOidcProviderForm/CreateOidcProviderForm';
import { useCreateOidcProviderForm } from '../../../hooks/Oidc/useCreateOidcProviderForm';

export const CreateIdentityProviderModal: CreateIdentityProviderModalComponent = ({
  onClose,
  isOpen,
  createOidcProvider,
  createSamlProvider,
  isCreating,
  error,
}) => {
  const intl = useIntl();

  const [providerType, setProviderType] = useState<IdentityProviderType | null>(null);

  const {
    control: samlFormControl,
    handleSubmit: samlFormHandleSubmit,
    formState: samlFormState,
    resetField: samlFormRestField,
    trigger,
    setValue,
  } = useCreateSamlProviderForm();

  const { fields, append, remove, update } = useFieldArray({
    control: samlFormControl,
    name: 'certificates',
  });

  const {
    control: oidcFormControl,
    handleSubmit: oidcFormHandleSubmit,
    formState: oidcFormState,
    setValue: oidcSetValue,
    trigger: oidcTrigger,
    watch: oidcWatch,
  } = useCreateOidcProviderForm();

  const isSubmitBtnDisabled = useMemo(() => {
    if (providerType) {
      if (providerType === IdentityProviderType.Oidc) {
        return !(oidcFormState.isValid && oidcFormState.isDirty);
      } else {
        return !(samlFormState.isValid && samlFormState.isDirty);
      }
    } else {
      return false;
    }
  }, [
    oidcFormState.isValid,
    samlFormState.isValid,
    oidcFormState.isDirty,
    samlFormState.isDirty,
    providerType,
  ]);

  const handleSubmitBtnClick = () => {
    if (!!providerType) {
      if (providerType === IdentityProviderType.Oidc) {
        // @ts-ignore
        oidcFormHandleSubmit(onOidcFormSubmitted)();
      } else {
        // @ts-ignore
        samlFormHandleSubmit(onSamlFormSubmitted)();
      }
    }
  };

  const onOidcFormSubmitted = (formData: CreateOidcIdentityProviderDto) => {
    const dataToSend =
      formData.flow === OidcAuthFlow.Code
        ? formData
        : { ...formData, usePKCE: undefined, clientSecret: undefined };
    createOidcProvider(dataToSend);
  };

  const onSamlFormSubmitted = (
    formData: Omit<CreateSamlIdentityProviderDto, 'certificates'> & {
      certificates: Array<{ value: string }>;
    },
  ) => {
    const createSamlIdentityProviderDto: CreateSamlIdentityProviderDto = {
      ...formData,
      certificates: formData.certificates.map((cert) => cert.value),
    };
    createSamlProvider(createSamlIdentityProviderDto);
  };

  return (
    <Modal
      data-testid="create-identity-provider-modal"
      title={intl.formatMessage(IdentityProvidersMessages.createIdentityProvider)}
      onClose={onClose}
      open={isOpen}
      footer={
        providerType ? (
          <Flexbox container justifyContent="flex-end">
            <Button type="submit" onClick={handleSubmitBtnClick} disabled={isSubmitBtnDisabled}>
              {isCreating
                ? intl.formatMessage(CommonMessages.creating)
                : intl.formatMessage(CommonMessages.create)}
            </Button>
          </Flexbox>
        ) : null
      }
    >
      <ErrorBox error={error} />
      <Flexbox container gutter={4} alignItems="flex-start" mb={5}>
        <Flexbox item>
          <Icon.Info size="lg" color={() => '#6E33A9'} />
        </Flexbox>
        <Flexbox item container gutter={2} direction="column">
          <Flexbox item>
            <Typography variant="body2">
              Hii Retail IAM supports authentication by any{' '}
              <Link to="https://openid.net/developers/certified/#OPServices" external>
                Certified OpenID 2.0 Identity Provider
              </Link>{' '}
              or{' '}
              <Link to="https://cloud.google.com/identity-platform/docs/web/saml" external>
                SAML Identity Provider{' '}
              </Link>
              <br />
              It allows users to authenticate to Hii Retail using the credentials already familiar
              to them for systems like Microsoft Azure AD, Okta, or Ping Federate.
            </Typography>
          </Flexbox>
        </Flexbox>
      </Flexbox>

      <Box py={4} mb={providerType ? 0 : 20}>
        <Select
          fullWidth
          hint="The type of the provider used by the 3rd party identity platform"
          onChange={(option: Option | null) => {
            if (option) {
              setProviderType(option.value as IdentityProviderType);
            }
          }}
          options={[
            { label: 'OIDC', value: IdentityProviderType.Oidc },
            { label: 'SAML', value: IdentityProviderType.Saml },
          ]}
          name="provider-type-select"
          label="Provider type"
          isSearchable={false}
          placeholder="Select provider type"
          required
          isClearable={false}
        />
      </Box>

      {providerType && providerType === IdentityProviderType.Saml && (
        <CreateSamlProviderForm
          control={samlFormControl}
          formState={samlFormState}
          fields={fields}
          append={append}
          remove={remove}
          update={update}
          resetField={samlFormRestField}
          trigger={trigger}
          setValue={setValue}
        />
      )}
      {providerType && providerType === IdentityProviderType.Oidc && (
        <CreateOidcProviderForm
          control={oidcFormControl}
          formState={oidcFormState}
          setValue={oidcSetValue}
          trigger={oidcTrigger}
          watch={oidcWatch}
        />
      )}
    </Modal>
  );
};
